import { addDoc, collection } from 'firebase/firestore';
import { db } from 'config/firebase';
import { smsTemplates } from './smsTemplates';

/**
 * Normalize a phone number to E.164 (defaulting to US +1) so the messages
 * collection / Twilio listener consumes a consistent format. Mirrors the
 * normalization in `useSendTextMessage`.
 */
const normalizePhoneNumber = (phone: string): string => {
  const cleaned = phone.replace(/[^\d+]/g, '');
  if (cleaned.startsWith('+')) return cleaned;
  const digits = cleaned.startsWith('1') ? cleaned.slice(1) : cleaned;
  return `+1${digits}`;
};

export interface SendTemplatedSmsResult {
  success: boolean;
  messageDocId?: string;
  body?: string;
  normalizedPhone?: string;
}

/**
 * Render a templated SMS and drop a doc into the `messages` collection that
 * the Twilio listener (`messageCreatedListener`) consumes to actually send.
 */
export const sendTemplatedSms = async <T extends keyof typeof smsTemplates>(
  to: string,
  templateName: T,
  params: Parameters<(typeof smsTemplates)[T]>[0],
  metadata: { sentBy?: string | null; type?: string; [key: string]: unknown } = {},
): Promise<SendTemplatedSmsResult> => {
  try {
    const template = smsTemplates[templateName];
    const { body } = await template(params as any);

    const normalizedPhone = normalizePhoneNumber(to.trim());
    const cleanMetadata = Object.fromEntries(
      Object.entries(metadata).filter(([, value]) => value !== undefined),
    );

    const docRef = await addDoc(collection(db, 'messages'), {
      to: normalizedPhone,
      body,
      timestamp: new Date().toISOString(),
      type: metadata.type || 'task_completion_text',
      scheduled: false,
      triggered_by: 'admin_portal',
      sent_by: metadata.sentBy || 'unknown',
      ...cleanMetadata,
    });

    return {
      success: true,
      messageDocId: docRef.id,
      body,
      normalizedPhone,
    };
  } catch (error) {
    console.error('Error sending templated SMS:', error);
    return { success: false };
  }
};
